import React, { useEffect, useRef, useState } from 'react';
import { Modal } from './ui/Modal';
import {
  pickSavePath,
  saveTextToPath,
  appendTextToPath,
  resultToCsv,
  resultToInsertSql,
  csvHeader,
  rowsToCsvLines,
} from './dbExportUtils';

const CHUNK_SIZE = 1000;
// Browser fallback has no append API, so whole-table exports buffer there —
// warn once the buffer stops being a rounding error.
const BUFFER_WARN_ROWS = 250_000;

/**
 * File export for the DB Manager grid: CSV or SQL INSERT dump of either the
 * currently loaded result set or (on table tabs) the entire table, paged
 * client-side through the tab's fetchChunk (honoring the active WHERE filter
 * and sort; the fetcher guarantees a deterministic order — keyset or
 * PK-ordered OFFSET — and pages via an opaque cursor). Tables where no total
 * order exists (chunkable=false) are fetched in ONE snapshot query instead.
 *
 * The destination is picked up front (Tauri save dialog) so a long
 * whole-table fetch can't end at a cancelled dialog, and chunks are appended
 * to the file as they arrive — the webview never holds the whole table plus
 * its serialized text. Outside Tauri it buffers and downloads at the end.
 */
export default function DbExportModal({
  open,
  onClose,
  engine,
  tableName, // null on query tabs
  result, // currently loaded { columns, rows }
  totalRows, // COUNT(*) when known (table tabs), else null
  fetchChunk, // async (cursor, limit) => { columns, rows, nextCursor } — table tabs only
  chunkable = true, // false: no total order available; single snapshot query
  // The grid's selected rows, result-shaped (pending edits applied), or null.
  // Offered as "Selected rows" and picked by default when present (owner,
  // 30.09.2026: "export only the selected rows").
  selection = null,
}) {
  const selectedCount = selection?.rows?.length ?? 0;
  const canExportTable = typeof fetchChunk === 'function';
  const [scope, setScope] = useState('current');
  const [format, setFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [fetchedRows, setFetchedRows] = useState(0);
  const [error, setError] = useState(null);
  const [doneMessage, setDoneMessage] = useState(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    if (open) {
      setScope(selectedCount > 0 ? 'selected' : 'current');
      setFormat('csv');
      setExporting(false);
      setFetchedRows(0);
      setError(null);
      setDoneMessage(null);
      cancelRef.current = false;
    }
  }, [open, selectedCount]); // the grid is covered while open, so the count only changes between opens

  if (!open) return null;

  const close = () => {
    cancelRef.current = true;
    onClose();
  };

  const runExport = async () => {
    if (exporting) return;
    setError(null);
    setDoneMessage(null);
    const ext = format === 'csv' ? 'csv' : 'sql';
    const base = (tableName || 'query-result').replace(/[/\\:]/g, '_');
    const filename = `${base}.${ext}`;
    const filters =
      format === 'csv'
        ? [{ name: 'CSV', extensions: ['csv'] }]
        : [{ name: 'SQL', extensions: ['sql'] }];

    let path;
    try {
      path = await pickSavePath({ filename, filters });
    } catch (e) {
      setError(String(e));
      return;
    }
    if (path === null) return; // dialog cancelled

    setExporting(true);
    setFetchedRows(0);
    cancelRef.current = false;
    const mime = format === 'csv' ? 'text/csv' : 'text/plain';
    const insertTable = tableName || 'query_result';
    try {
      if (scope === 'table' && canExportTable) {
        // In Tauri (path is a real string) each chunk is serialized and
        // appended as it arrives, so memory stays flat regardless of table
        // size. The browser fallback (path === undefined) has no append —
        // it buffers rows and downloads once at the end.
        const streamed = path !== undefined;
        const buffered = streamed ? null : [];
        let columns = null;
        let wrote = false;
        let count = 0;
        let cursor = null;
        for (;;) {
          if (cancelRef.current) {
            if (streamed && wrote) {
              setError(`Export cancelled — a partial file was left at ${path}.`);
            }
            setExporting(false);
            return;
          }
          // chunkable=false: no total order exists for this table, so paging
          // could duplicate/drop rows — fetch everything in one snapshot
          // query (limit null) and let nextCursor end the loop.
          // eslint-disable-next-line no-await-in-loop
          const res = await fetchChunk(cursor, chunkable ? CHUNK_SIZE : null);
          if (!columns) columns = res.columns;
          count += res.rows.length;
          if (streamed) {
            const text =
              format === 'csv'
                ? (wrote ? '' : csvHeader(columns)) + rowsToCsvLines(res.rows)
                : resultToInsertSql(engine, insertTable, columns, res.rows);
            if (!wrote) {
              // First write creates/truncates; later chunks append.
              // eslint-disable-next-line no-await-in-loop
              await saveTextToPath(path, { content: text, filename, mime });
            } else if (text) {
              // eslint-disable-next-line no-await-in-loop
              await appendTextToPath(path, text);
            }
            wrote = true;
          } else {
            for (const r of res.rows) buffered.push(r);
            if (buffered.length > BUFFER_WARN_ROWS) {
              console.warn(`[db-export] buffering ${buffered.length} rows in memory (no append API outside Tauri)`);
            }
          }
          setFetchedRows(count);
          if (res.nextCursor == null) break;
          cursor = res.nextCursor;
        }
        if (!streamed) {
          const content =
            format === 'csv'
              ? resultToCsv({ columns: columns || [], rows: buffered })
              : resultToInsertSql(engine, insertTable, columns || [], buffered);
          await saveTextToPath(path, { content, filename, mime });
        }
        setDoneMessage(
          `Exported ${count.toLocaleString()} row${count === 1 ? '' : 's'}${streamed ? ` to ${path}` : ''}.`
        );
      } else {
        const source = scope === 'selected' && selection ? selection : result;
        const columns = source?.columns || [];
        const rows = source?.rows || [];
        const content =
          format === 'csv'
            ? resultToCsv({ columns, rows })
            : resultToInsertSql(engine, insertTable, columns, rows);
        const saved = await saveTextToPath(path, { content, filename, mime });
        setDoneMessage(
          `Exported ${rows.length.toLocaleString()} row${rows.length === 1 ? '' : 's'}${saved.path ? ` to ${saved.path}` : ''}.`
        );
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setExporting(false);
    }
  };

  const radio = (name, checked, onChange, label, disabled = false, hint = null) => (
    <label
      className="ext-checkbox-label"
      style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {/* checkbox-brand: the Params-table custom control (circular radio variant) instead of the native paint */}
      <input type="radio" name={name} className="checkbox-brand cursor-pointer" checked={checked} disabled={disabled || exporting} onChange={onChange} />
      <span>{label}</span>
      {hint && <span style={{ fontSize: 11, color: '#71717a' }}>{hint}</span>}
    </label>
  );

  const currentCount = result?.rows?.length ?? 0;

  return (
    <Modal isOpen onClose={close} title="Export result set" subtitle={tableName ? `Table "${tableName}"` : 'Query result'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <div className="modal-logo-label" style={{ marginBottom: 6 }}>Scope</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {selectedCount > 0 &&
              radio('dbm-export-scope', scope === 'selected', () => setScope('selected'),
                'Selected rows', false, `${selectedCount.toLocaleString()} row${selectedCount === 1 ? '' : 's'}`)}
            {radio('dbm-export-scope', scope === 'current', () => setScope('current'),
              'Current result', false, `${currentCount.toLocaleString()} loaded row${currentCount === 1 ? '' : 's'}`)}
            {canExportTable &&
              radio('dbm-export-scope', scope === 'table', () => setScope('table'),
                'Entire table', false,
                (totalRows != null ? `~${totalRows.toLocaleString()} rows, honors filter & sort` : 'honors filter & sort') +
                  (chunkable ? '' : ' — no primary key, fetched in one query'))}
          </div>
        </div>
        <div>
          <div className="modal-logo-label" style={{ marginBottom: 6 }}>Format</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {radio('dbm-export-format', format === 'csv', () => setFormat('csv'), 'CSV')}
            {radio('dbm-export-format', format === 'sql', () => setFormat('sql'), 'SQL INSERT statements', false, '100 rows per statement')}
          </div>
        </div>

        {exporting && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
            <span className="dbm-spinner" aria-hidden style={{ width: 14, height: 14 }} />
            <span>
              {scope === 'table'
                ? `Fetching rows… ${fetchedRows.toLocaleString()}${totalRows != null ? ` of ~${totalRows.toLocaleString()}` : ''}`
                : 'Exporting…'}
            </span>
            {scope === 'table' && (
              <button
                type="button"
                className="dbm-pager-btn"
                onClick={() => {
                  cancelRef.current = true;
                }}
              >
                Cancel
              </button>
            )}
          </div>
        )}
        {error && <div className="dbm-error" style={{ padding: 0 }}>{error}</div>}
        {doneMessage && <div style={{ fontSize: 12, color: 'var(--accent)', wordBreak: 'break-all' }}>{doneMessage}</div>}

        <div className="modal-footer-row">
          <button
            type="button"
            onClick={close}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {doneMessage ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            disabled={exporting || (scope === 'current' && !result?.columns?.length)}
            onClick={runExport}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
